import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { 
  Church, Users, BookOpen, Bell, Trophy, LogOut, 
  Crown, Copy, Check, Loader, TrendingUp, Calendar,
  ChevronRight, Star, ChevronDown, ChevronUp, User, Plus, Award, CheckCircle, ArrowRight, X, Heart, Trash2
} from "lucide-react";
import { AvatarDisplay } from "./ParentDashboard";
import { useLanguage } from "../hooks/useLanguage";

export default function ChurchDashboard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, logout, login } = useAuth();
  const { t } = useLanguage();
  const [church, setChurch] = useState(null);
  const [stats, setStats] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [families, setFamilies] = useState([]);
  const [familyProgress, setFamilyProgress] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [expandedFamily, setExpandedFamily] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  useEffect(() => {
    // Complete pending login from registration
    const pendingAuth = sessionStorage.getItem("pending_church_auth");
    if (pendingAuth) {
      try {
        const { token, user } = JSON.parse(pendingAuth);
        login(token, user);
        sessionStorage.removeItem("pending_church_auth");
      } catch (e) {
        console.error("Error completing login:", e);
      }
    }
    
    loadData();
    // Check for subscription success
    if (searchParams.get("subscription") === "success") {
      setShowSuccessModal(true);
      // Clear the URL parameter
      searchParams.delete("subscription");
      setSearchParams(searchParams, { replace: true });
    }
  }, []);

  const loadData = async () => {
    try {
      const [churchRes, statsRes, subRes, familiesRes, progressRes] = await Promise.all([
        axios.get(`${API}/church/me`),
        axios.get(`${API}/church/stats`),
        axios.get(`${API}/church/subscription/status`),
        axios.get(`${API}/church/families`),
        axios.get(`${API}/church/family-progress`)
      ]);
      setChurch(churchRes.data);
      setStats(statsRes.data);
      setSubscription(subRes.data);
      setFamilies(familiesRes.data);
      setFamilyProgress(progressRes.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const copyInviteCode = () => {
    navigator.clipboard.writeText(church?.invite_code || "");
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const [showPlanModal, setShowPlanModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState("annual");
  const [processingCheckout, setProcessingCheckout] = useState(false);
  const [prayerRequests, setPrayerRequests] = useState([]);
  const [updatingPrayer, setUpdatingPrayer] = useState(null);

  const loadPrayerRequests = async () => {
    try {
      const { data } = await axios.get(`${API}/church/prayer-requests`);
      setPrayerRequests(data);
    } catch (e) { console.error(e); }
  };

  const updatePrayerStatus = async (requestId, status) => {
    setUpdatingPrayer(requestId);
    try {
      await axios.put(`${API}/church/prayer-requests/${requestId}`, { status });
      loadPrayerRequests();
    } catch (e) {
      alert(e.response?.data?.detail || "Could not update status");
    } finally {
      setUpdatingPrayer(null);
    }
  };

  const deletePrayerRequest = async (requestId) => {
    if (!window.confirm("Are you sure you want to delete this prayer request?")) return;
    setUpdatingPrayer(requestId);
    try {
      await axios.delete(`${API}/church/prayer-requests/${requestId}`);
      loadPrayerRequests();
    } catch (e) {
      alert(e.response?.data?.detail || "Could not delete prayer request");
    } finally {
      setUpdatingPrayer(null);
    }
  };

  useEffect(() => {
    loadPrayerRequests();
  }, [loading]);

  const handleSubscribe = async (planId) => {
    setProcessingCheckout(true);
    try {
      const { data } = await axios.post(`${API}/church/subscription/checkout`, { plan_id: planId || selectedPlan });
      window.location.href = data.checkout_url;
    } catch (e) {
      alert(e.response?.data?.detail || "Could not start checkout");
      setProcessingCheckout(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#F0F4FF" }}>
        <Loader size={48} className="animate-spin" color="#118AB2" />
      </div>
    );
  }

  const menuItems = [
    { icon: Users, label: t('families'), desc: `${stats?.total_families || 0} ${t('families').toLowerCase()}`, path: "/church/families", color: "#118AB2" },
    { icon: BookOpen, label: t('church_study'), desc: t('create_study'), path: "/church/study", color: "#9B5DE5" },
    { icon: Bell, label: t('announcements'), desc: t('send_announcement'), path: "/church/announcements", color: "#EF476F" },
    { icon: Trophy, label: t('leaderboard'), desc: t('top_learners'), path: "/church/leaderboard", color: "#FFD166" },
  ];

  return (
    <div className="min-h-screen pb-8" style={{ background: "#F0F4FF" }}>
      {/* Header */}
      <div className="px-4 pt-6 pb-12"
        style={{ background: "linear-gradient(135deg, #073B4C 0%, #118AB2 100%)" }}>
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="bg-white/20 rounded-2xl p-3">
                <Church size={28} color="white" strokeWidth={2.5} />
              </div>
              <div>
                <h1 className="text-2xl font-black text-white" style={{ fontFamily: "Fredoka, sans-serif" }}>
                  {church?.name}
                </h1>
                <p className="text-white/70 text-sm font-medium">{t('church_dashboard')}</p>
              </div>
            </div>
            <button onClick={handleLogout}
              className="bg-white/20 p-2.5 rounded-xl hover:bg-white/30 transition-colors"
              data-testid="logout-btn">
              <LogOut size={20} color="white" />
            </button>
          </div>

          {/* Invite Code */}
          <div className="bg-white/10 rounded-2xl p-4 flex items-center justify-between">
            <div>
              <p className="text-white/70 text-xs font-bold uppercase tracking-wider">{t('invite_code')}</p>
              <p className="text-white text-2xl font-black tracking-widest">{church?.invite_code}</p>
            </div>
            <button onClick={copyInviteCode}
              className="bg-white text-[#118AB2] font-bold px-4 py-2 rounded-xl flex items-center gap-2"
              data-testid="copy-code-btn">
              {copied ? <Check size={18} /> : <Copy size={18} />}
              {copied ? t('code_copied') : t('copy_code')}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 -mt-6">
        {/* Subscription Banner */}
        {!subscription?.is_active && (
          <div className="bg-gradient-to-r from-[#9B5DE5] to-[#EF476F] rounded-3xl p-5 mb-5 shadow-xl">
            <div className="flex items-center gap-4">
              <div className="bg-white/20 rounded-2xl p-3">
                <Crown size={28} color="white" strokeWidth={2.5} />
              </div>
              <div className="flex-1">
                <p className="text-white font-black" style={{ fontFamily: "Fredoka, sans-serif" }}>
                  {t('upgrade_church_premium')}
                </p>
                <p className="text-white/80 text-sm font-medium">
                  {t('manage_up_to_50')} • $39.99/{t('monthly')} or $339.99/{t('year')}
                </p>
              </div>
              <button onClick={() => setShowPlanModal(true)}
                className="bg-white text-[#9B5DE5] font-black px-5 py-2.5 rounded-xl"
                data-testid="subscribe-btn">
                {t('subscribe')}
              </button>
            </div>
          </div>
        )}

        {/* Plan Selection Modal */}
        {showPlanModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-3xl p-6 max-w-md w-full shadow-2xl">
              <h2 className="text-xl font-black text-[#073B4C] mb-1" style={{ fontFamily: "Fredoka, sans-serif" }}>
                Choose Your Plan
              </h2>
              <p className="text-gray-500 text-sm mb-4">Select a subscription plan for your church</p>
              
              <div className="space-y-3 mb-5">
                {subscription?.plans?.map((plan) => (
                  <button
                    key={plan.id}
                    type="button"
                    onClick={() => setSelectedPlan(plan.id)}
                    className={`w-full p-4 rounded-2xl border-2 transition-all text-left ${
                      selectedPlan === plan.id 
                        ? "border-[#118AB2] bg-[#118AB2]/5" 
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                    data-testid={`modal-plan-${plan.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          selectedPlan === plan.id ? "border-[#118AB2] bg-[#118AB2]" : "border-gray-300"
                        }`}>
                          {selectedPlan === plan.id && <Check size={12} color="white" strokeWidth={3} />}
                        </div>
                        <div>
                          <p className="font-bold text-[#073B4C]">{plan.name}</p>
                          <p className="text-xs text-gray-500">{plan.description}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-black text-lg text-[#073B4C]">${plan.price}</p>
                        <p className="text-xs text-gray-400">/{plan.interval}</p>
                      </div>
                    </div>
                    {plan.savings && (
                      <div className="mt-2 inline-block bg-green-100 text-green-700 text-xs font-black px-2 py-0.5 rounded-full">
                        SAVE {plan.savings}
                      </div>
                    )}
                  </button>
                ))}
              </div>

              <div className="flex gap-3">
                <button 
                  onClick={() => setShowPlanModal(false)}
                  className="flex-1 py-3 px-4 rounded-xl border-2 border-gray-200 font-bold text-gray-600 hover:bg-gray-50"
                  data-testid="cancel-modal-btn"
                >
                  Cancel
                </button>
                <button 
                  onClick={() => handleSubscribe(selectedPlan)}
                  disabled={processingCheckout}
                  className="flex-1 py-3 px-4 rounded-xl font-bold text-white disabled:opacity-50"
                  style={{ background: "linear-gradient(90deg, #118AB2, #06D6A0)" }}
                  data-testid="confirm-subscribe-btn"
                >
                  {processingCheckout ? "Processing..." : "Subscribe Now"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          <StatCard icon={Users} label={t('families')} value={stats?.total_families || 0} color="#118AB2" />
          <StatCard icon={Star} label={t('children')} value={stats?.total_children || 0} color="#06D6A0" />
          <StatCard icon={BookOpen} label={t('lessons_done')} value={stats?.total_lessons_completed || 0} color="#9B5DE5" />
          <StatCard icon={Calendar} label={t('today_lessons')} value={stats?.today_lessons_completed || 0} color="#FFD166" />
        </div>

        {/* Family Progress Section */}
        <div className="bg-white rounded-3xl shadow-xl overflow-hidden mb-5">
          <div className="p-4 border-b flex items-center justify-between" style={{ background: "linear-gradient(90deg, #D1FAE5, #E0F2FE)" }}>
            <div className="flex items-center gap-3">
              <Award size={24} color="#06D6A0" strokeWidth={2.5} />
              <div>
                <h3 className="font-black text-[#073B4C]" style={{ fontFamily: "Fredoka, sans-serif" }}>
                  {t('completed_lessons_by_family')}
                </h3>
                <p className="text-xs text-gray-500">{t('track_family_progress')}</p>
              </div>
            </div>
          </div>
          
          {familyProgress.length === 0 ? (
            <div className="p-8 text-center">
              <BookOpen size={40} className="mx-auto mb-2 opacity-40" color="#06D6A0" />
              <p className="text-gray-500 font-medium">{t('no_lessons_completed_yet')}</p>
              <p className="text-gray-400 text-sm">{t('families_appear_here')}</p>
            </div>
          ) : (
            <div className="divide-y max-h-72 overflow-y-auto">
              {familyProgress.map((family, idx) => (
                <div key={family.family_id} className="p-3 flex items-center gap-3 hover:bg-gray-50">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white text-sm ${
                    idx === 0 ? 'bg-yellow-500' : idx === 1 ? 'bg-gray-400' : idx === 2 ? 'bg-amber-600' : 'bg-gray-300'
                  }`}>
                    {idx + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-[#073B4C] truncate">{family.family_name}</p>
                    <p className="text-xs text-gray-400">{family.children_count} {family.children_count === 1 ? t('child') : t('kids')}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-black text-[#06D6A0]">{family.lessons_completed}</p>
                    <p className="text-xs text-gray-400 font-medium">{t('lessons')}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Prayer Requests Section */}
        <div className="bg-white rounded-3xl shadow-xl overflow-hidden mb-5">
          <div className="p-4 border-b flex items-center justify-between" style={{ background: "linear-gradient(90deg, #F0E6FF, #FFE4E6)" }}>
            <div className="flex items-center gap-3">
              <Heart size={24} color="#EF476F" strokeWidth={2.5} />
              <div>
                <h3 className="font-black text-[#073B4C]" style={{ fontFamily: "Fredoka, sans-serif" }}>
                  Prayer Requests
                </h3>
                <p className="text-xs text-gray-500">{prayerRequests.length} requests from families</p>
              </div>
            </div>
          </div>
          
          {prayerRequests.length === 0 ? (
            <div className="p-8 text-center">
              <Heart size={40} className="mx-auto mb-2 opacity-40" color="#EF476F" />
              <p className="text-gray-500 font-medium">No prayer requests yet</p>
              <p className="text-gray-400 text-sm">When families submit requests, they'll appear here</p>
            </div>
          ) : (
            <div className="divide-y max-h-96 overflow-y-auto">
              {prayerRequests.map((req) => (
                <div key={req.id} className="p-4 hover:bg-gray-50">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-bold text-[#073B4C]">{req.title}</p>
                        {req.is_important && (
                          <span className="bg-red-100 text-red-600 text-xs px-2 py-0.5 rounded-full font-bold">
                            Important
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mb-2">{req.description}</p>
                      <p className="text-xs text-gray-400">From: <span className="font-medium">{req.family_name}</span></p>
                    </div>
                    <div className="flex flex-col gap-1">
                      <button
                        onClick={() => updatePrayerStatus(req.id, 'praying')}
                        disabled={updatingPrayer === req.id || req.status === 'praying'}
                        className={`text-xs font-bold px-3 py-1.5 rounded-lg transition-all ${
                          req.status === 'praying' 
                            ? 'bg-purple-100 text-purple-700' 
                            : 'bg-gray-100 text-gray-600 hover:bg-purple-50 hover:text-purple-600'
                        }`}
                        data-testid={`praying-btn-${req.id}`}
                      >
                        {req.status === 'praying' ? '🙏 Praying' : 'Mark Praying'}
                      </button>
                      <button
                        onClick={() => updatePrayerStatus(req.id, 'answered')}
                        disabled={updatingPrayer === req.id || req.status === 'answered'}
                        className={`text-xs font-bold px-3 py-1.5 rounded-lg transition-all ${
                          req.status === 'answered' 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-gray-100 text-gray-600 hover:bg-green-50 hover:text-green-600'
                        }`}
                        data-testid={`answered-btn-${req.id}`}
                      >
                        {req.status === 'answered' ? '✓ Answered' : 'Mark Answered'}
                      </button>
                      <button
                        onClick={() => deletePrayerRequest(req.id)}
                        disabled={updatingPrayer === req.id}
                        className="text-xs font-bold px-3 py-1.5 rounded-lg transition-all bg-gray-100 text-red-500 hover:bg-red-50 hover:text-red-600"
                        data-testid={`delete-prayer-btn-${req.id}`}
                      >
                        <Trash2 size={12} className="inline mr-1" /> Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* All Families Section */}
        <div className="bg-white rounded-3xl shadow-xl overflow-hidden mb-5">
          <div className="p-4 border-b flex items-center justify-between" style={{ background: "linear-gradient(90deg, #E0F4FF, #F0E6FF)" }}>
            <div>
              <h3 className="font-black text-[#073B4C]" style={{ fontFamily: "Fredoka, sans-serif" }}>
                {t('church_families')}
              </h3>
              <p className="text-xs text-gray-500">{families.length} {t('families_registered')}</p>
            </div>
            <button onClick={() => navigate("/church/families")}
              className="bg-[#118AB2] text-white font-bold px-3 py-1.5 rounded-xl text-sm flex items-center gap-1"
              data-testid="manage-families-btn">
              <Plus size={16} /> {t('add')}
            </button>
          </div>
          
          {families.length === 0 ? (
            <div className="p-8 text-center">
              <Users size={40} className="mx-auto mb-2 opacity-40" color="#118AB2" />
              <p className="text-gray-500 font-medium">{t('no_families_yet')}</p>
              <p className="text-gray-400 text-sm">{t('share_invite_code')}: <span className="font-black">{church?.invite_code}</span></p>
            </div>
          ) : (
            <div className="divide-y max-h-96 overflow-y-auto">
              {families.map((family) => (
                <div key={family.id} className="p-3">
                  <div className="flex items-center gap-3">
                    <div className="bg-blue-100 rounded-full w-10 h-10 flex items-center justify-center">
                      <User size={20} color="#118AB2" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-[#073B4C] truncate">{family.name}</p>
                      <p className="text-xs text-gray-400 truncate">{family.email}</p>
                    </div>
                    <span className="text-xs font-bold text-gray-400 bg-gray-100 px-2 py-1 rounded-full">
                      {family.children_count} kids
                    </span>
                    {family.children?.length > 0 && (
                      <button onClick={() => setExpandedFamily(expandedFamily === family.id ? null : family.id)}
                        className="p-1.5 hover:bg-gray-100 rounded-lg">
                        {expandedFamily === family.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </button>
                    )}
                  </div>
                  
                  {/* Expanded children */}
                  {expandedFamily === family.id && family.children?.length > 0 && (
                    <div className="mt-2 ml-12 space-y-1">
                      {family.children.map((child) => (
                        <div key={child.id} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                          <AvatarDisplay avatar={child.avatar} size={28} />
                          <div>
                            <p className="font-bold text-xs text-[#073B4C]">{child.name}</p>
                            <p className="text-xs text-gray-400">
                              {child.age_group === "little" ? "Little Light" : child.age_group === "big" ? "Explorer" : "Faith Builder"}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Menu Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {menuItems.map((item) => (
            <button key={item.path}
              onClick={() => navigate(item.path)}
              className="bg-white rounded-2xl p-5 shadow-md flex items-center gap-4 hover:scale-[1.02] transition-transform text-left"
              data-testid={`menu-${item.label.toLowerCase()}`}>
              <div className="rounded-2xl p-3" style={{ background: `${item.color}20` }}>
                <item.icon size={28} color={item.color} strokeWidth={2.5} />
              </div>
              <div className="flex-1">
                <p className="font-black text-[#073B4C]" style={{ fontFamily: "Fredoka, sans-serif" }}>
                  {item.label}
                </p>
                <p className="text-gray-500 text-sm font-medium">{item.desc}</p>
              </div>
              <ChevronRight size={20} color="#9CA3AF" />
            </button>
          ))}
        </div>
      </div>

      {/* Subscription Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl text-center relative">
            <button 
              onClick={() => setShowSuccessModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <X size={24} />
            </button>
            
            <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{ background: "linear-gradient(135deg, #06D6A0, #118AB2)" }}>
              <CheckCircle size={44} color="white" strokeWidth={2.5} />
            </div>
            
            <h2 className="text-2xl font-black text-[#073B4C] mb-2" style={{ fontFamily: "Fredoka, sans-serif" }}>
              Subscription Active!
            </h2>
            <p className="text-gray-500 mb-6">
              Your church subscription is now active. You can manage up to 50 families!
            </p>
            
            <div className="bg-teal-50 rounded-2xl p-4 mb-6">
              <div className="flex items-center gap-3 justify-center">
                <Crown size={24} color="#118AB2" />
                <span className="font-black text-[#118AB2]">Church Premium Active</span>
              </div>
            </div>

            {/* Next Steps */}
            <div className="bg-gray-50 rounded-2xl p-4 mb-6 text-left">
              <p className="font-bold text-[#073B4C] mb-3 text-sm">Next Steps:</p>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <div className="w-5 h-5 rounded-full bg-[#118AB2] flex items-center justify-center text-white text-xs font-bold">1</div>
                  <span className="text-gray-600">Share your invite code with families</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <div className="w-5 h-5 rounded-full bg-[#06D6A0] flex items-center justify-center text-white text-xs font-bold">2</div>
                  <span className="text-gray-600">Create your first church Bible study</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <div className="w-5 h-5 rounded-full bg-[#9B5DE5] flex items-center justify-center text-white text-xs font-bold">3</div>
                  <span className="text-gray-600">Send a welcome announcement</span>
                </div>
              </div>
            </div>

            <button 
              onClick={() => setShowSuccessModal(false)}
              className="w-full text-white font-black py-4 rounded-2xl flex items-center justify-center gap-2"
              style={{ background: "linear-gradient(90deg, #118AB2, #06D6A0)" }}
              data-testid="close-success-modal-btn"
            >
              Get Started <ArrowRight size={20} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-white rounded-2xl p-4 shadow-md">
      <Icon size={24} color={color} strokeWidth={2.5} className="mb-2" />
      <p className="text-2xl font-black text-[#073B4C]">{value}</p>
      <p className="text-xs font-bold text-gray-400 uppercase">{label}</p>
    </div>
  );
}
